/**
 * PHI (Protected Health Information) Sanitizer for Frontend
 *
 * This module provides comprehensive sanitization of PHI data from error messages,
 * logs, and any output that might be exposed to console, error reporting, or monitoring.
 *
 * HIPAA Compliance: Ensures that no PHI is exposed in browser console, error tracking
 * services, or any frontend logging mechanisms.
 */

/**
 * Regular expression patterns for various PHI types
 */
const PHI_PATTERNS = {
  // Social Security Numbers
  SSN: /\b\d{3}-?\d{2}-?\d{4}\b/g,

  // Phone numbers (various formats)
  PHONE: /\b(?:\+?1[-.]?)?\(?([0-9]{3})\)?[-.]?([0-9]{3})[-.]?([0-9]{4})\b/g,

  // Email addresses
  EMAIL: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,

  // Dates of birth (YYYY-MM-DD or similar)
  DOB: /\b(?:19|20)\d{2}[-/](0[1-9]|1[0-2])[-/](0[1-9]|[12]\d|3[01])\b/g,

  // Medical Record Numbers
  MRN: /\b(?:MRN|mrn|medical[_\s]?record[_\s]?(?:number|num|no|#))[:\s]*([A-Z0-9-]{5,20})\b/gi,

  // Insurance numbers
  INSURANCE: /\b(?:insurance|policy)[_\s]?(?:number|num|no|#)[:\s]*([A-Z0-9-]{5,20})\b/gi,

  // UUIDs (patient IDs, appointment IDs, etc.)
  UUID: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,

  // Zip codes
  ZIP: /\b\d{5}(?:-\d{4})?\b/g,

  // Street addresses
  STREET: /\b\d+\s+(?:[A-Z][a-z]+\s+){1,3}(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Way)\b/gi,
};

/**
 * Field names that contain PHI and should be redacted
 */
const PHI_FIELD_NAMES = new Set([
  'ssn',
  'social_security',
  'social_security_number',
  'phone',
  'phone_number',
  'mobile',
  'telephone',
  'email',
  'email_address',
  'dob',
  'date_of_birth',
  'birth_date',
  'birthdate',
  'mrn',
  'medical_record_number',
  'insurance_number',
  'policy_number',
  'first_name',
  'firstName',
  'last_name',
  'lastName',
  'full_name',
  'fullName',
  'patient_name',
  'patientName',
  'name',
  'address',
  'street',
  'street_address',
  'streetAddress',
  'city',
  'state',
  'zip',
  'zipcode',
  'postal_code',
  'postalCode',
  'patient_id',
  'patientId',
  'appointment_id',
  'appointmentId',
  'doctor_id',
  'doctorId',
  'user_id',
  'userId',
  'password',
  'secret',
  'token',
  'api_key',
  'apiKey',
  'auth_token',
  'authToken',
]);

/**
 * Sanitize a string by replacing PHI patterns with redaction text
 */
export function sanitizeString(text: string, redactionText: string = '[REDACTED]'): string {
  if (typeof text !== 'string') {
    return text;
  }

  let sanitized = text;

  // Replace SSN
  sanitized = sanitized.replace(PHI_PATTERNS.SSN, `${redactionText}_SSN`);

  // Replace phone numbers
  sanitized = sanitized.replace(PHI_PATTERNS.PHONE, `${redactionText}_PHONE`);

  // Replace emails
  sanitized = sanitized.replace(PHI_PATTERNS.EMAIL, `${redactionText}_EMAIL`);

  // Replace DOB
  sanitized = sanitized.replace(PHI_PATTERNS.DOB, `${redactionText}_DOB`);

  // Replace MRN
  sanitized = sanitized.replace(PHI_PATTERNS.MRN, `MRN: ${redactionText}_MRN`);

  // Replace insurance numbers
  sanitized = sanitized.replace(PHI_PATTERNS.INSURANCE, `Insurance: ${redactionText}_INSURANCE`);

  // Replace UUIDs
  sanitized = sanitized.replace(PHI_PATTERNS.UUID, `${redactionText}_ID`);

  // Replace zip codes
  sanitized = sanitized.replace(PHI_PATTERNS.ZIP, `${redactionText}_ZIP`);

  // Replace street addresses
  sanitized = sanitized.replace(PHI_PATTERNS.STREET, `${redactionText}_ADDRESS`);

  return sanitized;
}

/**
 * Recursively sanitize an object by removing PHI fields and sanitizing values
 */
export function sanitizeObject<T = any>(obj: T, redactionText: string = '[REDACTED]'): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== 'object') {
    if (typeof obj === 'string') {
      return sanitizeString(obj, redactionText) as any;
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item, redactionText)) as any;
  }

  const sanitized: any = {};

  for (const [key, value] of Object.entries(obj)) {
    // Check if field name indicates PHI
    const keyLower = key.toLowerCase().replace(/-/g, '_');

    if (PHI_FIELD_NAMES.has(keyLower)) {
      sanitized[key] = `${redactionText}_${keyLower.toUpperCase()}`;
    } else if (value && typeof value === 'object') {
      sanitized[key] = sanitizeObject(value, redactionText);
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value, redactionText);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Sanitize an error object
 */
export function sanitizeError(error: Error | any, redactionText: string = '[REDACTED]'): string {
  if (!error) {
    return 'Unknown error';
  }

  // Get error message
  const message = error.message || error.toString?.() || String(error);

  // Sanitize the message
  let sanitizedMessage = sanitizeString(message, redactionText);

  // If error has additional properties, sanitize them
  if (typeof error === 'object') {
    const errorObj = { ...error };
    delete errorObj.stack; // Don't include stack traces in sanitized output

    const sanitizedError = sanitizeObject(errorObj, redactionText);

    // If there are sanitized details, include them
    if (Object.keys(sanitizedError).length > 0) {
      const details = JSON.stringify(sanitizedError);
      if (details !== '{}') {
        sanitizedMessage += ` | Details: ${details}`;
      }
    }
  }

  return sanitizedMessage;
}

/**
 * Sanitize a stack trace
 */
export function sanitizeStackTrace(stackTrace: string, redactionText: string = '[REDACTED]'): string {
  if (typeof stackTrace !== 'string') {
    return stackTrace;
  }

  // Split into lines to preserve structure
  const lines = stackTrace.split('\n');
  const sanitizedLines = lines.map((line) => {
    // Don't sanitize file paths and line numbers, but sanitize values
    if (line.trim().startsWith('at ')) {
      return line; // Keep stack frame lines as is
    }
    return sanitizeString(line, redactionText);
  });

  return sanitizedLines.join('\n');
}

/**
 * Create a safe error message for display to users
 */
export function createUserFriendlyError(_error: Error | any, errorId?: string): string {
  const baseMessage = 'An error occurred. Please try again or contact support if the issue persists.';

  if (errorId) {
    return `${baseMessage} (Error ID: ${errorId})`;
  }

  return baseMessage;
}

/**
 * Sanitize console.log arguments
 */
export function sanitizeLogArguments(...args: any[]): any[] {
  return args.map((arg) => {
    if (typeof arg === 'string') {
      return sanitizeString(arg);
    } else if (arg && typeof arg === 'object') {
      return sanitizeObject(arg);
    }
    return arg;
  });
}

/**
 * Create a sanitized console wrapper
 */
export const sanitizedConsole = {
  log: (...args: any[]) => {
    console.log(...sanitizeLogArguments(...args));
  },
  error: (...args: any[]) => {
    console.error(...sanitizeLogArguments(...args));
  },
  warn: (...args: any[]) => {
    console.warn(...sanitizeLogArguments(...args));
  },
  info: (...args: any[]) => {
    console.info(...sanitizeLogArguments(...args));
  },
  debug: (...args: any[]) => {
    console.debug(...sanitizeLogArguments(...args));
  },
};

/**
 * Check if a string contains potential PHI
 */
export function containsPHI(text: string | null | undefined): boolean {
  if (typeof text !== 'string') {
    return false;
  }

  // Check against all PHI patterns
  for (const pattern of Object.values(PHI_PATTERNS)) {
    if (pattern.test(text)) {
      return true;
    }
  }

  return false;
}

/**
 * Sanitize API error response
 */
export function sanitizeApiError(error: any): {
  message: string;
  status?: number;
  errorId?: string;
} {
  const sanitized: any = {
    message: 'An error occurred while processing your request.',
  };

  if (error?.response) {
    // Axios error
    sanitized.status = error.response.status;

    if (error.response.data?.error_id) {
      sanitized.errorId = error.response.data.error_id;
    }

    if (error.response.data?.message) {
      sanitized.message = sanitizeString(error.response.data.message);
    } else if (error.response.data?.detail) {
      sanitized.message = sanitizeString(error.response.data.detail);
    }
  } else if (error?.message) {
    sanitized.message = sanitizeString(error.message);
  }

  return sanitized;
}

/**
 * Default export with all sanitization functions
 */
export default {
  sanitizeString,
  sanitizeObject,
  sanitizeError,
  sanitizeStackTrace,
  sanitizeLogArguments,
  sanitizeApiError,
  createUserFriendlyError,
  containsPHI,
  sanitizedConsole,
};
